/**
 * The bookkeeping behind the light pass's `register*` calls: the lights on the canvas with their
 * ordinals, and the placements of the lit items. React state only, no GPU.
 */
import { useCallback, useRef, useState } from 'react';
import { placementId, } from './itemLightList.js';
/** The lowest ordinal `taken` has not handed out. */
function freeOrdinal(taken) {
    let ordinal = 0;
    while (taken.has(ordinal))
        ordinal += 1;
    return ordinal;
}
/**
 * The declared lights by ordinal. The lowest free ordinal is reused, so ordinals stay dense for the
 * 8-bit stencil, which keeps each light's shadow stamps apart in a pass.
 */
export function useLightRegistry() {
    const [lights, setLights] = useState(() => new Map());
    const taken = useRef(new Map()).current;
    const declare = useCallback((declaration) => {
        const ordinal = freeOrdinal(taken);
        taken.set(ordinal, declaration);
        setLights(new Map(taken));
        // A second release must not free the ordinal a later light has since been given: React's
        // strict double-invoke replays the cleanup.
        let released = false;
        return {
            ordinal,
            release: () => {
                if (released)
                    return;
                released = true;
                taken.delete(ordinal);
                setLights(new Map(taken));
            },
        };
    }, [taken]);
    return [lights, declare];
}
function placementDeclarations({ placement, readers }) {
    return { placement, reads: new Set(readers.keys()) };
}
function sameReads(a, b) {
    return a.size === b.size && [...a].every((read) => b.has(read));
}
/** The placements of the lit items by `placementId`, counted so each stands while an item holds it. */
export function usePlacementRegistry() {
    const [placements, setPlacements] = useState(() => new Map());
    const counts = useRef(new Map()).current;
    const publish = useCallback(() => {
        setPlacements((previous) => {
            const next = new Map([...counts].map(([id, count]) => [id, placementDeclarations(count)]));
            const unchanged = previous.size === next.size &&
                [...next].every(([id, declared]) => {
                    const held = previous.get(id);
                    return held !== undefined && sameReads(held.reads, declared.reads);
                });
            return unchanged ? previous : next;
        });
    }, [counts]);
    const declare = useCallback((placement, read) => {
        const id = placementId(placement);
        const count = counts.get(id) ?? { placement, readers: new Map() };
        count.readers.set(read, (count.readers.get(read) ?? 0) + 1);
        counts.set(id, count);
        publish();
        // As above: strict double-invoke replays a cleanup, and a second decrement would drop a live
        // declaration.
        let released = false;
        return () => {
            if (released)
                return;
            released = true;
            const left = count.readers.get(read) - 1;
            if (left > 0)
                count.readers.set(read, left);
            else
                count.readers.delete(read);
            if (count.readers.size === 0)
                counts.delete(id);
            publish();
        };
    }, [counts, publish]);
    return [placements, declare];
}
//# sourceMappingURL=lightRegistry.js.map